import React, {Component} from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ModuleMargin} from 'Frontend/components/ModuleMargins';

import {parseToUri} from 'Admin/utils/formParsers';
import {BadgeBackground} from 'Frontend/assets/images/icons';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, iconAsBackground, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';

/**
 * Returns an random angle position for the background icon.
 *
 * @returns {Number} Random angle number.
 */
const getRandomAngle = () => {
    // eslint-disable-next-line @nfq/no-magic-numbers
    const randomTop = Math.floor(Math.random() * (360 - 270 + 1) + 270);
    // eslint-disable-next-line @nfq/no-magic-numbers
    const randomBottom = Math.floor(Math.random() * (180 - 90 + 1) + 90);

    // eslint-disable-next-line @nfq/no-magic-numbers
    if (Math.floor(Math.random() * (2 - 1 + 1) + 1) === 1) {
        return randomBottom;
    }

    return randomTop;
};

/**
 * Badges
 *
 * @property {*} prop Prop comment.
 *
 * @class Badges
 * @extends {Component}
 */
class Badges extends Component {
    static propTypes = {
        badges: PropTypes.array.isRequired,
        isInGroup: PropTypes.bool,
        name: PropTypes.string
    }

    static defaultProps = {
        isInGroup: false,
        name: null
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof Badges
     */
    render() {
        const {badges, isInGroup, name} = this.props;

        return (
            <BadgesElement isInGroup={isInGroup}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                <a id={parseToUri(name)} />
                <BadgesContainer>
                    {badges.map(badge => {
                        const {description, hasText, icon, iconText, overline, withBackground} = badge;

                        return (
                            <BadgeWrap key={description} overline={overline}>
                                <BadgeIcon overline={overline} withBackground={withBackground} suppressHydrationWarning>
                                    {hasText && iconText}
                                    {!hasText && <Image alt={icon.alt} src={icon.publicPath.src} title={icon.title} />}
                                </BadgeIcon>
                                <TextWrapper overline={overline}>
                                    {overline && <OverlineText>{overline}</OverlineText>}
                                    <Description overline={overline}>{description}</Description>
                                </TextWrapper>
                            </BadgeWrap>
                        );
                    })}
                </BadgesContainer>
            </BadgesElement>
        );
    }
}

export default Badges;

const BadgesElement = styled(ModuleMargin)`
    height: 100%;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    position: relative;
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.DESKTOP.UP} {
        max-width: max(1320px, ${pxToVw(1320, 'd')});
        padding: ${({isInGroup}) => (isInGroup ? `0 max(110px, ${pxToVw(110, 'd')}) 0 max(110px, ${pxToVw(110, 'd')})` : '0')};
        width: 100%;
    }
`;

const BadgesContainer = styled.div`
    --gap: ${clampCss(45, 'm')};
    display: inline-flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
    margin: calc(-1 * var(--gap)) 0 0 0;
    width: 100%;

    & > * {
        margin-top: var(--gap);
    }

    ${MEDIA.TABLET.UP} {
        --gap: ${clampCss(32, 't')};
        flex-direction: row;
    }

    ${MEDIA.DESKTOP.UP} {
        --gap: max(32px, ${pxToVw(32, 'd')});
        flex-direction: row;
    }
`;

const BadgeWrap = styled.div`
    align-items: center;
    display: flex;
    flex-direction: ${({overline}) => (overline ? 'column' : 'row')};
    margin-left: auto;
    margin-right: auto;
    min-width: 267px;
    width: 100%;

    ${MEDIA.TABLET.UP} {
        flex: 0 0 ${clampCss(170, 't')};
        flex-direction: column;
        min-width: ${clampCss(170, 't')};
        width: auto;
    }

    ${MEDIA.DESKTOP.UP} {
        flex: 0 0 max(267px, ${pxToVw(267, 'd')});
        flex-direction: column;
        min-width: max(267px, ${pxToVw(267, 'd')});
        width: auto;
    }
`;

const BadgeIcon = styled.div.attrs(() => ({style: {'--angle': `${getRandomAngle()}deg`}}))`
    ${FONTS.SEMIBOLD}
    align-items: center;
    display: flex;
    font-size: ${({overline}) => (overline ? clampCss(32, 'm') : clampCss(24, 'm'))};
    height: ${clampCss(95, 'm')};
    justify-content: center;
    letter-spacing: 1.33px;
    position: relative;
    text-transform: uppercase;
    width: ${({overline}) => (overline ? '100%' : clampCss(95, 'm'))};
    z-index: 1;

    &:before {
        background-image: ${({withBackground}) => (withBackground ? `url(${iconAsBackground(BadgeBackground)})` : null)};
        background-position: center center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        height: ${clampCss(73, 'm')};
        left: calc(50% - ${clampCss(36.5, 'm')});
        position: absolute;
        top: calc(50% - ${clampCss(36.5, 'm')});
        transform: rotate(var(--angle));
        transform-origin: 50% 50%;
        width: ${clampCss(73, 'm')};
        z-index: -1;
    }

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(20, 't')};
        height: ${clampCss(67, 't')};
        letter-spacing: ${clampCss(0.84, 't')};
        width: 100%;

        &:before {
            height: ${clampCss(67, 't')};
            left: calc(50% - ${clampCss(33.5, 't')});
            top: 0;
            width: ${clampCss(67, 't')};
        }
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(32px, ${pxToVw(32, 'd')});
        height: max(106px, ${pxToVw(106, 'd')});
        letter-spacing: max(1.33px, ${pxToVw(1.33, 'd')});
        top: 0;

        &:before {
            height: max(106px, ${pxToVw(106, 'd')});
            left: calc(50% - max(53px, ${pxToVw(53, 'd')}));
            top: 0;
            width: max(106px, ${pxToVw(106, 'd')});
        }
    }
`;

const Image = styled.img`
    height: ${clampCss(45, 'm')};
    width: ${clampCss(45, 'm')};

    ${MEDIA.TABLET.UP} {
        height: ${clampCss(35, 't')};
        width: ${clampCss(35, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        height: max(55px, ${pxToVw(55, 'd')});
        width: max(55px, ${pxToVw(55, 'd')});
    }
`;

const TextWrapper = styled.div`
    margin: ${({overline}) => (overline ? '0' : `0 0 0 ${clampCss(25, 'm')}`)};

    ${MEDIA.TABLET.UP} {
        margin: 0;
    }

    ${MEDIA.DESKTOP.UP} {
        margin: 0;
    }
`;

const OverlineText = styled.p`
    ${FONTS.SEMIBOLD}
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    margin: ${clampCss(19, 'm')} 0 0 0;
    text-align: center;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        margin: ${clampCss(19, 't')} 0 0 0;
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(12px, ${pxToVw(12, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(16.8px, ${pxToVw(16.8, 'd')});
        margin: max(19px, ${pxToVw(19, 'd')});
    }
`;

const Description = styled.p`
    ${FONTS.REGULAR}
    font-size: ${clampCss(16, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(22, 'm')};
    margin: ${({overline}) => (overline ? `${clampCss(8, 'm')} 0 0 0` : '0')};
    text-align: ${({overline}) => (overline ? 'center' : 'left')};

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(16, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(22, 't')};
        margin: ${({overline}) => (overline ? `${clampCss(8, 't')} 0 0 0` : `${clampCss(19, 't')} 0 0 0`)};
        text-align: center;
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(16px, ${pxToVw(16, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(22px, ${pxToVw(22, 'd')});
        margin: ${({overline}) => (overline ? `max(8px, ${pxToVw(8, 'd')}) 0 0 0` : `max(19px, ${pxToVw(19, 'd')}) 0 0 0`)};
    }
`;